import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ChartLineUp, ChatCircleDots, UserPlus, CheckCircle, Trophy, ArrowRight, GraduationCap, Info, CursorClick, Lightning, Check, Copy, Flame } from "@phosphor-icons/react";
import { PageHeader, StatCard, SectionCard, EmptyState } from "../components/ui/kaizen";

const ICONS = {
  conversations: ChatCircleDots, leads_inbox: UserPlus, converti: CheckCircle,
  leads_essai: GraduationCap, members: Trophy,
};

function FunnelBar({ stage, max }) {
  const Icon = ICONS[stage.key] || ChartLineUp;
  const pct = max > 0 ? Math.max(6, Math.round((stage.value / max) * 100)) : 6;
  return (
    <div className="flex flex-col gap-1.5" data-testid={`funnel-stage-${stage.key}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-2 text-sm text-zinc-300 font-manrope">
          <Icon size={16} weight="duotone" className="text-amber-500" /> {stage.label}
        </span>
        <span className="text-lg font-bold font-cabinet text-zinc-50">{(stage.value || 0).toLocaleString("fr-CA")}</span>
      </div>
      <div className="h-3 rounded-full bg-zinc-800 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-amber-600 to-amber-400 transition-all duration-700"
          style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-zinc-500 font-manrope">{stage.hint}</span>
    </div>
  );
}

export default function ConversionFunnelModule() {
  const [data, setData] = useState(null);
  const [clicks, setClicks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [relancing, setRelancing] = useState(null);

  const load = async () => {
    setLoading(true); setError(false);
    try {
      const [res, clk] = await Promise.all([
        api.get("/crm/funnel"),
        api.get("/social-inbox/link-clicks").catch(() => ({ data: null })),
      ]);
      setData(res.data);
      setClicks(clk.data);
      api.post("/social-inbox/conversion-alerts/seen").catch(() => {});
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const relance = async (token) => {
    setRelancing(token);
    try {
      const { data: res } = await api.post(`/social-inbox/link-clicks/${token}/relance`);
      if (res.sent) {
        toast.success("Relance envoyée automatiquement au prospect ! 🥋");
      } else {
        try { await navigator.clipboard.writeText(res.message); } catch (e) { /* clipboard indispo */ }
        toast.success("Message de relance généré et copié — colle-le dans ta messagerie.", {
          description: res.message?.slice(0, 120) + (res.message?.length > 120 ? "…" : ""),
          duration: 8000,
        });
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de générer la relance pour le moment.");
    } finally {
      setRelancing(null);
    }
  };

  const s = data?.summary || {};
  const rates = data?.rates || {};
  const socialMax = Math.max(...(data?.social_funnel || []).map((x) => x.value), 1);
  const ovMax = Math.max(...(data?.overview || []).map((x) => x.value), 1);

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="conversion-funnel-module">
      <PageHeader
        title="Conversion globale"
        icon={ChartLineUp}
        breadcrumbs={[{ label: "CRM & relations" }, { label: "Conversion globale" }]}
        subtitle="Du DM jusqu'au membre actif — visualise le rendement réel de tes réseaux sociaux."
        actions={
          <button onClick={load} data-testid="funnel-refresh"
            className="px-4 py-2 rounded-lg border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-100 text-sm">
            Actualiser
          </button>
        }
      />

      {error ? (
        <EmptyState icon={ChartLineUp} title="Données indisponibles"
          description="Impossible de charger l'entonnoir de conversion pour le moment."
          actionLabel="Réessayer" onAction={load} />
      ) : loading ? (
        <div className="text-center text-zinc-500 py-16 text-sm">Chargement…</div>
      ) : (
        <div className="flex flex-col gap-8">
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Conversations DM" value={(s.conversations || 0).toLocaleString("fr-CA")} icon={ChatCircleDots} />
            <StatCard label="Leads via Inbox" value={(s.leads_inbox || 0).toLocaleString("fr-CA")} icon={UserPlus} />
            <StatCard label="Convertis Inbox" value={(s.convos_converti || 0).toLocaleString("fr-CA")} icon={CheckCircle} />
            <StatCard label="Membres actifs" value={(s.members_active || 0).toLocaleString("fr-CA")} icon={Trophy} />
          </div>

          {clicks && clicks.total_links > 0 && (
            <SectionCard title="Prospects chauds — ont cliqué le lien de réservation" icon={CursorClick}
              description={`${clicks.clicked_count} à relancer${clicks.reengaged_count > 0 ? ` · 🔥 ${clicks.reengaged_count} re-engagé(s)` : ""} · ${clicks.booked_count || 0} déjà converti(s) · taux de clic ${clicks.click_rate}%`}>
              {clicks.items.length === 0 ? (
                <p className="text-sm text-zinc-400 font-manrope">Aucun prospect chaud en attente. Les prospects qui cliquent le lien de la 1ère touche IA sans réserver apparaîtront ici — relance-les en 1 clic.</p>
              ) : (
                <div className="flex flex-col divide-y divide-zinc-800" data-testid="link-clicks-list">
                  {clicks.items.map((it) => (
                    <div key={it.token} className={`flex items-center justify-between gap-3 py-3 text-sm ${it.reengaged ? "-mx-2 px-2 rounded-lg bg-red-500/5 border-l-2 border-red-500" : ""}`} data-testid={`hot-prospect-${it.token}`}>
                      <span className="flex items-center gap-2 text-zinc-200 min-w-0">
                        <span className={`h-2 w-2 rounded-full animate-pulse shrink-0 ${it.reengaged ? "bg-red-500" : "bg-amber-500"}`} />
                        {it.reengaged && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 text-[10px] font-bold uppercase tracking-wide shrink-0" data-testid={`reengaged-badge-${it.token}`}>
                            <Flame size={11} weight="fill" /> Re-engagé
                          </span>
                        )}
                        <span className="truncate font-medium">{it.contact_name}</span>
                        <span className="text-zinc-500 shrink-0">· {it.intent === "prive" ? "Cours privé" : "Cours d'essai"} · {it.click_count} clic(s)</span>
                      </span>
                      <span className="flex items-center gap-3 shrink-0">
                        <span className="text-xs text-zinc-500 font-manrope hidden md:inline">{it.last_clicked_at ? new Date(it.last_clicked_at).toLocaleString("fr-CA") : ""}</span>
                        {it.relance_sent ? (
                          <span className="flex items-center gap-1 text-xs text-emerald-400 font-manrope" data-testid={`relance-done-${it.token}`}>
                            <Check size={14} weight="bold" /> Relancé{it.relance_count > 1 ? ` ×${it.relance_count}` : ""}
                          </span>
                        ) : (
                          <button onClick={() => relance(it.token)} disabled={relancing === it.token}
                            data-testid={`relance-btn-${it.token}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-semibold transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed">
                            {relancing === it.token ? (
                              <>Génération…</>
                            ) : (
                              <>{it.can_auto_send ? <Lightning size={14} weight="fill" /> : <Copy size={14} weight="bold" />} Relance 1-clic{it.relance_count > 0 ? ` (à nouveau)` : ""}</>
                            )}
                          </button>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SectionCard title="Canal social (Inbox)" icon={ChatCircleDots}
              description="Rendement réel de tes réseaux sociaux">
              {data?.social_active ? (
                <>
                  <div className="flex flex-col gap-6">
                    {(data?.social_funnel || []).map((st) => <FunnelBar key={st.key} stage={st} max={socialMax} />)}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-400 font-manrope pt-2 border-t border-zinc-800">
                    <span className="text-amber-400 font-bold">{rates.social_convos_to_lead || 0}%</span>
                    des conversations deviennent un lead <ArrowRight size={12} />
                  </div>
                </>
              ) : (
                <div className="flex items-start gap-3 text-sm text-zinc-400 font-manrope p-2" data-testid="social-funnel-empty">
                  <Info size={20} weight="duotone" className="text-amber-500 shrink-0 mt-0.5" />
                  <p>Ton Inbox social vient de démarrer. Dès que les DM Facebook/Instagram arriveront et seront convertis en leads, ce canal affichera ton rendement réseau en temps réel.</p>
                </div>
              )}
            </SectionCard>

            <SectionCard title="Vue d'ensemble acquisition" icon={GraduationCap}
              description="Leads d'essai et membres actifs">
              <div className="flex flex-col gap-6">
                {(data?.overview || []).map((st) => <FunnelBar key={st.key} stage={st} max={ovMax} />)}
              </div>
              <div className="flex items-center gap-2 text-xs text-zinc-500 font-manrope pt-2 border-t border-zinc-800">
                <Info size={12} /> Totaux factuels (membres incluant les contrats migrés).
              </div>
            </SectionCard>
          </div>
        </div>
      )}
    </div>
  );
}
